import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { HotpolyMesh } from '../../../worker/actor/actor-messages';
import fs from './polygon-fragment.glsl?raw';
import vs from './polygon-vert.glsl?raw';

// create a custom style layer to implement the WebGL content
export class SolidPolygonLayer implements CustomLayerInterface {
    readonly type = 'custom';
    private aPos: GLint = 0;
    private aIntensity: GLint = 0;
    private featureCount: number = 0;
    private vertexBuffer: WebGLBuffer | null = null;
    private indexBuffer: WebGLBuffer | null = null;
    private intensityBuffer: WebGLBuffer | null = null;
    private gl: WebGL2RenderingContext | null = null;
    private map: MaplibreMap | null = null;
    private _opacity = 1.0;
    private ramp: WebGLTexture | null = null;
    private shaderMap = new Map();
    constructor(readonly id: string) {}

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    get opacity() {
        return this._opacity;
    }

    getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vertexSource = vs
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        const fragmentSource = fs;

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vertexSource);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.aIntensity = gl.getAttribLocation(program, 'a_intensity');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    // method called when the layer is added to the map
    // Search for StyleImageInterface in https://maplibre.org/maplibre-gl-js/docs/API/
    async onAdd(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext) {
        this.map = map;
        this.gl = gl as WebGL2RenderingContext;
        this.indexBuffer = gl.createBuffer();
        this.vertexBuffer = gl.createBuffer();
        this.intensityBuffer = gl.createBuffer();
        this.ramp = gl.createTexture();
    }

    setData(mesh: HotpolyMesh) {
        const gl = this.gl!;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, mesh.vertices, gl.STATIC_DRAW);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indexes, gl.STATIC_DRAW);
        this.featureCount = mesh.indexes.length;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.intensityBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, mesh.severity, gl.STATIC_DRAW);

        this.map?.triggerRepaint();
    }

    setRamp(data: Uint8Array) {
        if (!this.gl) {
            return;
        }

        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.ramp);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, data.byteLength / 4, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);

        this.map?.triggerRepaint();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        this.gl = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.indexBuffer);
        gl.deleteBuffer(this.intensityBuffer);
        gl.deleteTexture(this.ramp);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.aIntensity);

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }
    }

    // method fired on each animation frame
    render(gl: WebGL2RenderingContext | WebGLRenderingContext, args: CustomRenderMethodInput) {
        if (!this.vertexBuffer || this.featureCount === 0) {
            return;
        }

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData);
            gl.useProgram(program);

            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );
            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_debug'), 0.0);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.ramp);
            gl.uniform1i(gl.getUniformLocation(program, 'u_ramp'), 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.intensityBuffer);
            gl.enableVertexAttribArray(this.aIntensity);
            (gl as WebGL2RenderingContext).vertexAttribIPointer(this.aIntensity, 1, gl.UNSIGNED_BYTE, 0, 0);

            gl.drawElements(gl.TRIANGLES, this.featureCount, gl.UNSIGNED_INT, 0);
        });
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}
